/**
 * MOECOSPLAY CAROUSEL ENHANCEMENTS
 * Enhanced JavaScript functionality for product carousels
 */

class EnhancedSliderComponent extends HTMLElement {
    constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.autoplayToggle = null;
        this.autoplayInterval = null;
        this.autoplayDelay = 5000; // 5 seconds
        this.isAutoplayEnabled = false;
        this.isPaused = false;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.scrollTimeout = null;

        if (!this.slider) return;

        this.init();
    }

    init() {
        // Add navigation state classes
        this.updateNavigationState();

        // Setup intersection observer for lazy loading
        this.setupLazyLoading();

        // Setup touch gestures
        this.setupTouchGestures();

        // Setup keyboard navigation
        this.setupKeyboardNavigation();

        // Setup scroll tracking
        this.setupScrollTracking();

        // Setup autoplay toggle (optional)
        this.setupAutoplay();

        // Setup ARIA live regions
        this.setupAriaLiveRegion();

        // Update on scroll
        this.slider.addEventListener("scroll", () => {
            this.updateNavigationState();
            this.handleScrolling();
        });

        // Update on resize
        window.addEventListener(
            "resize",
            this.debounce(() => {
                this.updateNavigationState();
            }, 250),
        );

        // Pause autoplay on hover
        if (this.slider) {
            this.slider.addEventListener("mouseenter", () =>
                this.pauseAutoplay(),
            );
            this.slider.addEventListener("mouseleave", () =>
                this.resumeAutoplay(),
            );
        }

        // Add loaded class to images when they load
        this.setupImageLoading();
    }

    /**
     * Update navigation state (start/end position indicators)
     */
    updateNavigationState() {
        if (!this.slider) return;

        const isAtStart = this.slider.scrollLeft <= 10;
        const isAtEnd =
            this.slider.scrollLeft >=
            this.slider.scrollWidth - this.slider.clientWidth - 10;

        this.classList.toggle("at-start", isAtStart);
        this.classList.toggle("at-end", isAtEnd);
        this.classList.toggle("has-navigation", !isAtStart || !isAtEnd);

        // Update button states
        if (this.prevButton) {
            this.prevButton.disabled = isAtStart;
            this.prevButton.setAttribute("aria-disabled", isAtStart);
        }

        if (this.nextButton) {
            this.nextButton.disabled = isAtEnd;
            this.nextButton.setAttribute("aria-disabled", isAtEnd);
        }
    }

    /**
     * Setup lazy loading with Intersection Observer
     */
    setupLazyLoading() {
        if (!("IntersectionObserver" in window)) return;

        const options = {
            root: this.slider,
            rootMargin: "50px",
            threshold: 0.01,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const slide = entry.target;
                    slide.classList.add("in-view");

                    // Load images
                    const lazyImages = slide.querySelectorAll(
                        'img[loading="lazy"]',
                    );
                    lazyImages.forEach((img) => {
                        if (img.dataset.src) {
                            img.src = img.dataset.src;
                            img.classList.add("loaded");
                        }
                    });

                    // Optionally stop observing once loaded
                    observer.unobserve(slide);
                }
            });
        }, options);

        this.sliderItems.forEach((slide) => {
            slide.setAttribute("data-lazy", "true");
            observer.observe(slide);
        });
    }

    /**
     * Setup touch gesture handlers
     */
    setupTouchGestures() {
        this.slider.addEventListener(
            "touchstart",
            (e) => {
                this.touchStartX = e.changedTouches[0].screenX;
            },
            { passive: true },
        );

        this.slider.addEventListener(
            "touchend",
            (e) => {
                this.touchEndX = e.changedTouches[0].screenX;
                this.handleSwipe();
            },
            { passive: true },
        );
    }

    /**
     * Handle swipe gestures
     */
    handleSwipe() {
        const swipeThreshold = 50;
        const diff = this.touchStartX - this.touchEndX;

        if (Math.abs(diff) > swipeThreshold) {
            if (diff > 0) {
                // Swipe left - next slide
                this.nextSlide();
            } else {
                // Swipe right - previous slide
                this.previousSlide();
            }
        }
    }

    /**
     * Setup keyboard navigation
     */
    setupKeyboardNavigation() {
        this.slider.setAttribute("tabindex", "0");

        this.slider.addEventListener("keydown", (e) => {
            switch (e.key) {
                case "ArrowLeft":
                    e.preventDefault();
                    this.previousSlide();
                    this.announceSlideChange("previous");
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    this.nextSlide();
                    this.announceSlideChange("next");
                    break;
                case "Home":
                    e.preventDefault();
                    this.goToSlide(0);
                    this.announceSlideChange("first");
                    break;
                case "End":
                    e.preventDefault();
                    this.goToSlide(this.sliderItems.length - 1);
                    this.announceSlideChange("last");
                    break;
            }
        });
    }

    /**
     * Navigate to next slide
     */
    nextSlide() {
        if (!this.slider) return;

        const slideWidth = this.sliderItems[0]?.offsetWidth || 0;
        const gap = parseFloat(getComputedStyle(this.slider).gap) || 0;
        const scrollAmount = slideWidth + gap;

        this.slider.scrollBy({
            left: scrollAmount,
            behavior: "smooth",
        });
    }

    /**
     * Navigate to previous slide
     */
    previousSlide() {
        if (!this.slider) return;

        const slideWidth = this.sliderItems[0]?.offsetWidth || 0;
        const gap = parseFloat(getComputedStyle(this.slider).gap) || 0;
        const scrollAmount = slideWidth + gap;

        this.slider.scrollBy({
            left: -scrollAmount,
            behavior: "smooth",
        });
    }

    /**
     * Go to specific slide
     */
    goToSlide(index) {
        if (!this.slider || !this.sliderItems[index]) return;

        const targetSlide = this.sliderItems[index];
        targetSlide.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
        });
    }

    /**
     * Setup scroll tracking for UI state
     */
    setupScrollTracking() {
        this.slider.addEventListener("scroll", () => {
            clearTimeout(this.scrollTimeout);
            this.classList.add("scrolling");

            this.scrollTimeout = setTimeout(() => {
                this.classList.remove("scrolling");
            }, 150);
        });
    }

    /**
     * Setup autoplay functionality
     */
    setupAutoplay() {
        // Check if reduced motion is preferred
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        // Create autoplay toggle button (optional - can be added to template)
        const existingToggle = this.querySelector(".slider-autoplay-toggle");
        if (existingToggle) {
            this.autoplayToggle = existingToggle;
            this.autoplayToggle.addEventListener("click", () =>
                this.toggleAutoplay(),
            );
        }
    }

    /**
     * Start autoplay
     */
    startAutoplay() {
        if (this.autoplayInterval) return;

        this.isAutoplayEnabled = true;
        this.classList.add("autoplay-enabled");

        this.autoplayInterval = setInterval(() => {
            if (!this.isPaused) {
                const isAtEnd =
                    this.slider.scrollLeft >=
                    this.slider.scrollWidth - this.slider.clientWidth - 10;

                if (isAtEnd) {
                    // Loop back to start
                    this.goToSlide(0);
                } else {
                    this.nextSlide();
                }
            }
        }, this.autoplayDelay);
    }

    /**
     * Stop autoplay
     */
    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }

        this.isAutoplayEnabled = false;
        this.classList.remove("autoplay-enabled");
    }

    /**
     * Toggle autoplay
     */
    toggleAutoplay() {
        if (this.isAutoplayEnabled) {
            this.stopAutoplay();
            if (this.autoplayToggle) {
                this.autoplayToggle.setAttribute(
                    "aria-label",
                    "Start autoplay",
                );
            }
        } else {
            this.startAutoplay();
            if (this.autoplayToggle) {
                this.autoplayToggle.setAttribute("aria-label", "Stop autoplay");
            }
        }
    }

    /**
     * Pause autoplay temporarily
     */
    pauseAutoplay() {
        this.isPaused = true;
        this.classList.add("autoplay-paused");
    }

    /**
     * Resume autoplay
     */
    resumeAutoplay() {
        this.isPaused = false;
        this.classList.remove("autoplay-paused");
    }

    /**
     * Setup ARIA live region for screen readers
     */
    setupAriaLiveRegion() {
        const liveRegion = document.createElement("div");
        liveRegion.className = "slider-live-region";
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("aria-atomic", "true");
        this.appendChild(liveRegion);
        this.liveRegion = liveRegion;
    }

    /**
     * Announce slide changes to screen readers
     */
    announceSlideChange(direction) {
        if (!this.liveRegion) return;

        const currentIndex = this.getCurrentSlideIndex();
        const totalSlides = this.sliderItems.length;

        let message = "";
        switch (direction) {
            case "next":
                message = `Moved to slide ${currentIndex + 1} of ${totalSlides}`;
                break;
            case "previous":
                message = `Moved to slide ${currentIndex + 1} of ${totalSlides}`;
                break;
            case "first":
                message = `Moved to first slide`;
                break;
            case "last":
                message = `Moved to last slide`;
                break;
            default:
                message = `Slide ${currentIndex + 1} of ${totalSlides}`;
        }

        this.liveRegion.textContent = message;
    }

    /**
     * Get current slide index based on scroll position
     */
    getCurrentSlideIndex() {
        if (!this.slider || this.sliderItems.length === 0) return 0;

        const scrollLeft = this.slider.scrollLeft;
        const slideWidth = this.sliderItems[0].offsetWidth;
        return Math.round(scrollLeft / slideWidth);
    }

    /**
     * Setup image loading states
     */
    setupImageLoading() {
        const images = this.querySelectorAll('img[loading="lazy"]');

        images.forEach((img) => {
            if (img.complete) {
                img.classList.add("loaded");
            } else {
                img.addEventListener("load", () => {
                    img.classList.add("loaded");
                });
            }
        });
    }

    /**
     * Debounce utility function
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Cleanup on disconnect
     */
    disconnectedCallback() {
        this.stopAutoplay();

        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
    }
}

// Register the custom element if not already defined
if (!customElements.get("enhanced-slider-component")) {
    customElements.define("enhanced-slider-component", EnhancedSliderComponent);
}

/**
 * Initialize enhanced sliders on page load
 */
document.addEventListener("DOMContentLoaded", () => {
    // Upgrade existing slider-component elements to enhanced version
    const sliders = document.querySelectorAll("slider-component");

    sliders.forEach((slider) => {
        // Add enhanced class for CSS targeting
        slider.classList.add("slider-enhanced");

        // Initialize enhanced features if not already initialized
        if (!slider.hasAttribute("data-enhanced")) {
            const enhancedSlider = new EnhancedSliderComponent();

            // Copy attributes and children
            Array.from(slider.attributes).forEach((attr) => {
                enhancedSlider.setAttribute(attr.name, attr.value);
            });

            while (slider.firstChild) {
                enhancedSlider.appendChild(slider.firstChild);
            }

            // Replace original with enhanced version
            slider.parentNode?.replaceChild(enhancedSlider, slider);
            enhancedSlider.setAttribute("data-enhanced", "true");
        }
    });
});

/**
 * Add skip carousel link for accessibility
 */
function addSkipCarouselLink() {
    const sliders = document.querySelectorAll(
        "slider-component, enhanced-slider-component",
    );

    sliders.forEach((slider, index) => {
        const skipLink = document.createElement("a");
        skipLink.href = `#carousel-end-${index}`;
        skipLink.className = "skip-carousel";
        skipLink.textContent = "Skip carousel";

        const endMarker = document.createElement("div");
        endMarker.id = `carousel-end-${index}`;
        endMarker.setAttribute("tabindex", "-1");

        slider.parentNode?.insertBefore(skipLink, slider);
        slider.parentNode?.insertBefore(endMarker, slider.nextSibling);
    });
}

// Add skip links after DOM is loaded
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", addSkipCarouselLink);
} else {
    addSkipCarouselLink();
}

/**
 * Prefers reduced motion support
 */
const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
);

function handleReducedMotionChange(e) {
    const sliders = document.querySelectorAll(
        "slider-component, enhanced-slider-component",
    );

    sliders.forEach((slider) => {
        if (e.matches) {
            slider.classList.add("no-animations");
            // Stop any autoplay
            if (slider.stopAutoplay) {
                slider.stopAutoplay();
            }
        } else {
            slider.classList.remove("no-animations");
        }
    });
}

prefersReducedMotion.addEventListener("change", handleReducedMotionChange);
handleReducedMotionChange(prefersReducedMotion);

/**
 * Export for use in other modules
 */
if (typeof module !== "undefined" && module.exports) {
    module.exports = { EnhancedSliderComponent };
}
